// Library Methods
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import DefaultDrawer from "src/components/shared/drawers/DefaultDrawer/DefaultDrawer";
import TextInputField from "src/components/shared/inputs/TextInputField/TextInputField";
import ExamSliderList from "src/components/containers/shared/ExamSliderList/ExamSliderList";
import PackageOrderSection from "src/components/containers/shared/PackageOrderSection/PackageOrderSection";
import SimulationSliderList from "src/components/containers/shared/SimulationSliderList/SimulationSliderList";
import VideoSliderList from "src/components/containers/shared/VideoSliderList/VideoSliderList";
import CustomIconButton from "src/components/shared/buttons/CustomIconButton.tsx/CustomIconButton";
import { CloseIcon, EyeIcon, PlayIcon, VRIcon } from "src/assets/icons";

// Styles
import {
  ModalCustomConfirm,
  getSurfaceTokens,
  getRequiredErrorColor,
} from "src/styles/mui/muiStylesHelper";

// Contexts
import { useExamSliderContext } from "src/contexts/exams/ExamSliderContext";
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Hooks
import { useComponentDimensions } from "src/hooks/window/useComponentDimensions";

type ExamPackageSliderType = {
  title: string;
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
  packageName: string;
  setPackageName: React.Dispatch<React.SetStateAction<string>>;
};

const ExamPackageSlider = ({
  title,
  isOpen,
  isLoading,
  onClose,
  onConfirm,
  packageName,
  setPackageName,
}: ExamPackageSliderType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { surface, onSurface } = getSurfaceTokens(theme);
  const { selectedExamCards } = useExamSliderContext();
  const { clinicInfo } = useClinicContext();
  const canUseExamPackaging = clinicInfo?.licensing?.canUseExamPackaging;

  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    const lensSimCards = selectedExamCards.filter((card) =>
      card.examFieldType.includes("simulation")
    );
    const hasOnlySimCards = Boolean(
      lensSimCards.length !== 0 &&
        lensSimCards.length === selectedExamCards.length
    );

    if (isOpen && hasOnlySimCards) {
      setSelectedTab(1);
    }

    const videoCards = selectedExamCards.filter((card) =>
      card.examFieldType.includes("video")
    );
    const hasOnlyVideoCards = Boolean(
      videoCards.length !== 0 && videoCards.length === selectedExamCards.length
    );

    if (isOpen && hasOnlyVideoCards) {
      setSelectedTab(2);
    }
  }, [isOpen, selectedExamCards]);

  const dimensions = useComponentDimensions({
    componentId: "#default-drawer-header",
  });

  const tabs = useMemo(
    () => [
      {
        id: 0,
        label: t("Exams"),
        icon: <EyeIcon height="20px" width="20px" />,
        showTab: true,
      },
      {
        id: 1,
        label: t("Simulations"),
        icon: <VRIcon height="20px" width="20px" />,
        showTab:
          clinicInfo?.licensing?.canUseIOLSimulations ||
          clinicInfo?.licensing?.canUseProgressiveSimulations,
      },
      {
        id: 2,
        label: t("Videos"),
        icon: <PlayIcon height="20px" width="20px" />,
        showTab: clinicInfo?.licensing?.canPlayVideos,
      },
    ],
    [
      t,
      clinicInfo?.licensing?.canPlayVideos,
      clinicInfo?.licensing?.canUseIOLSimulations,
      clinicInfo?.licensing?.canUseProgressiveSimulations,
    ]
  );

  // showTab is false for licensing-gated tabs the clinic can't use
  const activeTabs = useMemo(() => tabs.filter((tab) => tab.showTab), [tabs]);

  const shouldDisableSave = useMemo(
    () =>
      Boolean(!selectedExamCards?.length || !packageName?.length) || isLoading,
    [selectedExamCards, packageName, isLoading]
  );

  return (
    <DefaultDrawer
      key={title}
      isOpen={isOpen}
      onClose={() => {
        setSelectedTab(0);
        onClose();
      }}
      showCloseButton={false}
      title={`${title}${packageName ? ` - ${packageName}` : ""}`}
      customStyles={{
        width: { xs: "100%", md: "80%" },
        height: "100%",
        ...(theme.palette.mode === "dark" && {
          backgroundColor: "#5C6076",
          backgroundImage: "none",
        }),
      }}
      extraHeaderButtons={[
        <Box
          sx={{
            display: "flex",
            gap: "8px",
            flexDirection: "row",
          }}
        >
          <CustomIconButton
            variant="light"
            icon={<CloseIcon />}
            isDisabled={isLoading}
            onClick={() => {
              setSelectedTab(0);
              onClose();
            }}
          />

          <Button
            disabled={shouldDisableSave}
            sx={ModalCustomConfirm}
            onClick={() => {
              setSelectedTab(0);
              onConfirm();
            }}
            fullWidth
          >
            {t("Save")}
          </Button>
        </Box>,
      ]}
    >
      <Grid
        position="sticky"
        top={`${dimensions?.dynamicHeight ?? 59}px`}
        zIndex={100}
        display="flex"
        flexDirection="column"
        gap="8px"
        padding="8px 16px"
        sx={{
          backgroundColor: theme.palette.mode === "dark" ? "#35394F" : surface,
          borderBottom: "1px solid #435B6D7A",
        }}
      >
        <Grid display="flex" width={{ xs: "100%", sm: "50%" }}>
          <Box display="flex" flexDirection="column" gap="4px" width="100%">
            <Typography fontSize="12px">
              {t("Exam Package Name")}
              <span style={{ color: getRequiredErrorColor(theme) }}>*</span>
            </Typography>
            <TextInputField
              id="exam-package-name-input"
              required
              value={packageName}
              onChange={(event) => setPackageName(event.target.value)}
              placeholder={t("Enter a value")}
              error={!packageName?.length}
              helperText={
                !packageName?.length ? t("Package name is required") : undefined
              }
            />
          </Box>
        </Grid>
        {canUseExamPackaging && <PackageOrderSection />}
        {canUseExamPackaging && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "20px",
              margin: "0 -16px", // cancel the parent container's padding
              padding: "8px 16px 0px",
              borderTop:
                theme.palette.mode === "dark"
                  ? "1px solid #5B6D7A"
                  : "1px solid #435B6D7A",
            }}
          >
            {activeTabs.map((tab) => {
              const isSelected = selectedTab === tab.id;
              const tabColor = isSelected ? "#E2772E" : onSurface;

              return (
                <Button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id)}
                  startIcon={tab.icon}
                  disableRipple
                  sx={{
                    minWidth: 0,
                    padding: 0,
                    color: tabColor,
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: "#E2772E",
                    },
                  }}
                >
                  <Typography
                    fontSize="12px"
                    fontWeight={600}
                    sx={{
                      textTransform: "uppercase",
                    }}
                  >
                    {tab.label}
                  </Typography>
                </Button>
              );
            })}
          </Box>
        )}
      </Grid>

      <Grid
        item
        display="flex"
        flexDirection="column"
        padding="16px"
        gap="16px"
      >
        {selectedTab === 0 && <ExamSliderList />}
        {selectedTab === 1 && <SimulationSliderList />}
        {selectedTab === 2 && <VideoSliderList />}
      </Grid>
    </DefaultDrawer>
  );
};

export default ExamPackageSlider;
